<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentWrapper class="jumbotron">
      <UiComponentContainer
        class="inner"
      >
        <UiComponentWrapper class="copy">
          <AtomButton
            size="l"
            :to="localePath('legal-center')"
            type="text"
          >
            <UiComponentIcon type="arrow-left" />{{
              props.parentPage.backToLivestormLegalCenter
            }}
          </AtomButton>
          <UiComponentHeading>{{ props.page.title }}</UiComponentHeading>
          <UiComponentParagraph class="description">
            {{
              props.page.description
            }}
          </UiComponentParagraph>
          <UiComponentParagraph class="updated-at">
            {{ updatedAt }}
          </UiComponentParagraph>
        </UiComponentWrapper>
        <UiComponentWrapper class="nav">
          <NuxtLink
            v-for="{ id, title, slug } in props.allArticles"
            :key="`article-${id}`"
            class="item"
            :to="
              localePath({
                name: 'slug',
                params: {
                  slug,
                },
              })
            "
          >
            {{ title }}
          </NuxtLink>
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Content -->
    <UiComponentContainer
      class="main"
      :start="1"
      :end="1"
    >
      <StructuredText
        class="structured-text"
        :data="props.page.content"
        :render-block="renderBlock"
      />
    </UiComponentContainer>

    <!-- Section > FAQ -->
    <UiComponentContainer
      v-if="faqItems.length > 0"
      class="faq"
      :start="1"
      :end="1"
    >
      <UiComponentHeading :level="2">
        {{ props.componentGlobal.faqTitle }}
      </UiComponentHeading>
      <OrganismAccordion :items="displayedFaqItems" />
      <AtomButton
        v-if="faqItems.length > 5"
        type="secondary"
        @click="toggleFaqItems"
      >
        {{
          showMoreFaqItems
            ? props.componentGlobal.faqShowLessQuestions
            : props.componentGlobal.faqShowMoreQuestions
        }}
      </AtomButton>
    </UiComponentContainer>

    <!-- Section > Push -->
    <UiComponentWrapper class="push">
      <UiComponentImage
        v-if="
          props.parentPage.pushBackground
            && pushBackgroundHeight
            && pushBackgroundWidth
        "
        :key="`${pushBackgroundHeight}-${pushBackgroundWidth}`"
        :alt="props.parentPage.pushBackground.alt"
        class="background"
        :expected-size="pushBackgroundWidth / 3"
        :extra-params="{
          fit: 'crop',
          h: pushContainerHeight,
          w: pushContainerWidth,
          rect: `${pushBackgroundX},${pushBackgroundY},${pushBackgroundWidth},${pushBackgroundHeight}`,
        }"
        :height="pushBackgroundHeight"
        :lazyload="false"
        :orientation="
          pushBackgroundWidth > pushBackgroundHeight ? 'landscape' : 'portrait'
        "
        :quality="[100, 80, 80]"
        :source="props.parentPage.pushBackground.url"
        use-natural-height
        use-natural-width
        :width="pushBackgroundWidth"
      />
      <UiComponentContainer class="inner">
        <UiComponentHeading
          as="paragraph"
          :level="3"
        >
          {{
            props.parentPage.pushTitle
          }}
        </UiComponentHeading>
        <UiComponentParagraph>{{ props.parentPage.pushText }}</UiComponentParagraph>
        <AtomButton
          action="Button clicked"
          :attributes="{
            action: props.parentPage.pustCtaLink,
          }"
          label="Contact section CTA"
          size="l"
          :to="props.parentPage.pustCtaLink"
        >
          {{ props.parentPage.pustCtaSlot }}
        </AtomButton>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="ourFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import { StructuredText } from 'vue-datocms'

// Props
const props = defineProps({
  allArticles: {
    required: true,
    type: Array,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  home: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
  parentPage: {
    required: true,
    type: Object,
  },
})

// Composables
const { page } = useAnalyticsJS()
const { updateListItems } = useBreadcrumb()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Meta
useHead({
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': props.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': props.parentPage.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('legal-center')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': props.page.trackingBreadcrumbTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const pushBackgroundHeight = ref(null)
const pushBackgroundWidth = ref(null)
const pushBackgroundScale = ref(1)
const pushBackgroundX = ref(0)
const pushBackgroundY = ref(0)
const pushContainerHeight = ref(0)
const pushContainerWidth = ref(0)
const pushObserver = ref(null)
const showMoreFaqItems = ref(false)

// Computed Properties
const displayedFaqItems = computed(() => {
  const length = showMoreFaqItems.value ? faqItems.value.length : 5
  return faqItems.value.slice(0, length)
})

const faqItems = computed(() => {
  return (props.parentPage.faqItems || []).map(item => ({
    title: item.title,
    content: $md.render(item.text),
  }))
})

const format = computed(() => {
  switch (locale.value) {
    case 'fr':
      return 'D MMMM YYYY'
    default:
      return 'MMMM D, YYYY'
  }
})

const ourFeatures = computed(() => {
  return [
    props.componentGlobal.noCreditCard,
    props.componentGlobal.noInstall,
  ]
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaGetStartedForFree,
      }
})

const updatedAt = computed(() => {
  return `${props.parentPage.lastUpdateOn} ${formatDate(props.page.lastUpdate)}`
})

// Methods
function formatDate(date) {
  const locales = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
  }

  const selectedLocale = locales[locale.value] || locales.en

  return new Intl.DateTimeFormat(selectedLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

function onObservePushSection() {
  const dpr = 3
  const { clientWidth } = document.documentElement
  const { offsetHeight, offsetWidth } = document.querySelector('.page .push')
  const originalWidth
    = (props.parentPage?.pushBackground?.width || 3840) / 3

  pushBackgroundHeight.value = offsetHeight * dpr
  pushBackgroundWidth.value = offsetWidth * dpr
  pushContainerHeight.value = offsetHeight
  pushContainerWidth.value = offsetWidth

  switch (true) {
    case clientWidth < 768:
      pushBackgroundScale.value = originalWidth / 1413
      pushBackgroundHeight.value = Math.round(
        offsetHeight * dpr * pushBackgroundScale.value,
      )
      pushBackgroundWidth.value = Math.round(
        offsetWidth * dpr * pushBackgroundScale.value,
      )
      pushBackgroundX.value = 411 / 1413
      pushBackgroundY.value = 276 / 1131
      break
    case clientWidth >= 768 && clientWidth < 1328:
      pushBackgroundScale.value = originalWidth / 2298
      pushBackgroundHeight.value = Math.round(
        offsetHeight * dpr * pushBackgroundScale.value,
      )
      pushBackgroundWidth.value = Math.round(
        offsetWidth * dpr * pushBackgroundScale.value,
      )
      pushBackgroundX.value = 643 / 2298
      pushBackgroundY.value = 523 / 1839
      break
    case clientWidth >= 1328 && clientWidth < originalWidth:
      pushBackgroundScale.value = originalWidth / 2678
      pushBackgroundHeight.value = Math.round(
        offsetHeight * dpr * pushBackgroundScale.value,
      )
      pushBackgroundWidth.value = Math.round(
        offsetWidth * dpr * pushBackgroundScale.value,
      )
      pushBackgroundX.value = 'center'
      pushBackgroundY.value = Math.round(
        774 * dpr * pushBackgroundScale.value,
      )
      break
    default:
      pushBackgroundScale.value = 1
      pushBackgroundHeight.value = Math.round(offsetHeight * dpr)
      pushBackgroundWidth.value = Math.round(offsetWidth * dpr)
      pushBackgroundX.value = 'center'
      pushBackgroundY.value = 'middle'
  }
}

function renderBlock({ record, key }) {
  switch (record.__typename) {
    case 'MoleculeTextOnlyRecord':
      return h('div', {
        key,
        class: 'text-only',
        innerHTML: $md.render(record.content),
      })
    case 'OrganismTableRecord':
      return h('div', {
        key,
        class: 'table',
        innerHTML: record.content,
      })
    default:
      return null
  }
}

function toggleFaqItems() {
  showMoreFaqItems.value = !showMoreFaqItems.value
}

// Lifecycle hooks
onBeforeUnmount(() => {
  pushObserver.value.unobserve(document.querySelector('.page .push'))
})

onMounted(() => {
  page(props.page.trackName || props.page.trackNameFallback)
  updateListItems([
    {
      item: localePath('index'),
      name: props.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('legal-center'),
      name: props.parentPage.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: props.page.trackingBreadcrumbTitle,
    },
  ])

  pushObserver.value = new ResizeObserver(onObservePushSection)
  pushObserver.value.observe(document.querySelector('.page .push'))
})
</script>

<style lang="postcss" scoped>
.page {
  @apply relative flex-col items-center;

  .jumbotron {
    @apply items-center justify-center -mt-14 pt-[88px] pb-16 bg-[#E1EAEF];
    background: linear-gradient(180deg, rgba(210, 221, 228, 0.90) 0%, rgba(240, 246, 250, 0.90) 77.66%, rgba(210, 221, 228, 0.90) 120.4%);

    @screen xl {
      @apply -mt-28 pt-[144px];
    }

    .inner {
      @apply flex-col gap-8;

      @screen xl {
        @apply flex-row justify-center gap-10;
      }

      .copy {
        @apply flex-col;

        @screen xl {
          @apply w-span7;
        }

        .button {
          @apply self-start h-[50px] px-0 font-bold text-grey-blue-600;

          &:hover {
            :deep(i) {
              @apply -translate-x-1;
            }
          }

          :deep(i) {
            @apply font-bold text-base text-grey-blue-600 mr-2 leading-none transform transition-transform duration-300 ease-in-out !important;
          }
        }

        .h1 {
          @apply mt-4;
        }

        .description {
          @apply mt-2 text-grey-blue-600 leading-7;
        }

        .updated-at {
          @apply mt-4 text-grey-blue-600 text-sm leading-6;
        }
      }

      .nav {
        @apply flex-col self-start p-6 bg-white/60 border-2 border-white rounded-3xl;

        @screen xl {
          @apply w-span3;
        }

        .item {
          @apply flex px-3 py-2 font-medium text-grey-blue-600 text-sm leading-6 rounded-xs;

          &.router-link-exact-active {
            @apply bg-blizzard-200 text-blizzard-900;
          }

          &:hover {
            @apply text-blizzard-900;
          }
        }
      }
    }
  }

  .main {
    @apply flex-col mt-14;

    @screen md {
      @apply mt-26;
    }

    :deep(.structured-text) {
      @apply flex flex-col gap-20 w-full;

      .table {
        @apply flex flex-col w-full -mt-14 overflow-scroll;

        table {
          @apply flex flex-col h-auto w-[1060px] bg-white border border-grey-blue-200 rounded-xs overflow-hidden;

          tbody {
            @apply flex flex-col;

            tr {
              @apply flex border-b border-grey-blue-200;

              &:last-child {
                @apply border-b-0;
              }

              th {
                @apply flex flex-grow flex-shrink basis-0 p-2 bg-blizzard-800 text-white font-medium text-origin leading-7;
              }

              td {
                @apply flex flex-col flex-grow flex-shrink basis-0 p-2 bg-white text-grey-blue-600 font-normal whitespace-pre-line;
              }
            }
          }
        }
      }

      .text-only {
        @apply flex flex-col w-full text-left;

        h2,
        h3,
        h4,
        h5,
        h6,
        .h2,
        .h3,
        .h4,
        .h5,
        .h6 {
          @apply font-medium text-winter-green-900 whitespace-pre-line;

          b,
          strong {
            @apply font-medium text-livestorm-blue-700;
          }
        }

        h2,
        .h2 {
          @apply text-2xl leading-8;

          @screen mdminus {
            font-size: 28px;
            line-height: 34px;
          }

          @screen mdplus {
            @apply text-3xl leading-10;
          }
        }

        h3,
        .h3 {
          @apply text-xl leading-7;

          @screen mdminus {
            @apply text-2xl leading-8;
          }

          @screen mdplus {
            @apply text-2xl leading-8;
          }
        }

        h4,
        .h4 {
          @apply text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }

        h5,
        h6,
        .h5,
        .h6 {
          @apply text-origin leading-7;
        }

        ol {
          li {
            @apply font-normal text-grey-blue-600 text-origin leading-7 list-decimal list-inside;

            &::marker {
              @apply text-origin leading-7 font-medium text-grey-blue-600;
            }

            a {
              @apply contents;
            }
          }
        }

        p {
          @apply font-normal text-grey-blue-600 text-origin leading-7;
        }

        ul {
          li {
            @apply font-normal text-grey-blue-600 text-origin leading-7 list-disc list-inside;

            &::marker {
              @apply text-origin leading-7 font-medium text-grey-blue-600;
            }

            a {
              @apply contents;
            }
          }
        }

        strong {
          @apply font-medium text-winter-green-900;
        }

        a {
          @apply text-livestorm-blue-700;

          &:hover {
            @apply text-livestorm-blue-800;
          }

          &.button {
            &.primary {
              @apply text-white;
            }
          }
        }

        h1 + h1,
        h1 + h2,
        h1 + h3,
        h2 + h1,
        h2 + h2,
        h2 + h3,
        h3 + h1,
        h3 + h2,
        h3 + h3,
        h4 + h1,
        h4 + h2,
        h4 + h3,
        h5 + h1,
        h5 + h2,
        h5 + h3,
        h6 + h1,
        h6 + h2,
        h6 + h3,
        .h1 + .h1,
        .h1 + .h2,
        .h1 + .h3,
        .h2 + .h1,
        .h2 + .h2,
        .h2 + .h3,
        .h3 + .h1,
        .h3 + .h2,
        .h3 + .h3,
        .h4 + .h1,
        .h4 + .h2,
        .h4 + .h3,
        .h5 + .h1,
        .h5 + .h2,
        .h5 + .h3,
        .h6 + .h1,
        .h6 + .h2,
        .h6 + .h3 {
          @apply mt-4;
        }

        h1 + h4,
        h1 + h5,
        h1 + h6,
        h2 + h4,
        h2 + h5,
        h2 + h6,
        h3 + h4,
        h3 + h5,
        h3 + h6,
        h4 + h4,
        h4 + h5,
        h5 + h6,
        h5 + h4,
        h5 + h5,
        h5 + h6,
        h5 + h6,
        h6 + h4,
        h6 + h5,
        h6 + h6,
        .h1 + .h4,
        .h1 + .h5,
        .h1 + .h6,
        .h2 + .h4,
        .h2 + .h5,
        .h2 + .h6,
        .h3 + .h4,
        .h3 + .h5,
        .h3 + .h6,
        .h4 + .h4,
        .h4 + .h5,
        .h5 + .h6,
        .h5 + .h4,
        .h5 + .h5,
        .h5 + .h6,
        .h5 + .h6,
        .h6 + .h4,
        .h6 + .h5,
        .h6 + .h6 {
          @apply mt-4;
        }

        h1 + ol,
        h2 + ol,
        h3 + ol,
        h4 + ol,
        h5 + ol,
        h6 + ol,
        h1 + p,
        h2 + p,
        h3 + p,
        h4 + p,
        h5 + p,
        h6 + p,
        h1 + ul,
        h2 + ul,
        h3 + ul,
        h4 + ul,
        h5 + ul,
        h6 + ul,
        .h1 + ol,
        .h2 + ol,
        .h3 + ol,
        .h4 + ol,
        .h5 + ol,
        .h6 + ol,
        .h1 + p,
        .h2 + p,
        .h3 + p,
        .h4 + p,
        .h5 + p,
        .h6 + p,
        .h1 + ul,
        .h2 + ul,
        .h3 + ul,
        .h4 + ul,
        .h5 + ul,
        .h6 + ul,
        p + ol,
        p + ul {
          @apply mt-4;
        }

        ol + h1,
        ol + h2,
        p + h1,
        p + h2,
        ul + h1,
        ul + h2,
        ol + .h1,
        ol + .h2,
        p + .h1,
        p + .h2,
        ul + .h1,
        ul + .h2 {
          @apply mt-20;
        }

        ol + h3,
        ol + h4,
        ol + h5,
        ol + h6,
        p + h3,
        p + h4,
        p + h5,
        p + h6,
        ul + h3,
        ul + h4,
        ul + h5,
        ul + h6,
        ol + .h3,
        ol + .h4,
        ol + .h5,
        ol + .h6,
        p + .h3,
        p + .h4,
        p + .h5,
        p + .h6,
        ul + .h3,
        ul + .h4,
        ul + .h5,
        ul + .h6 {
          @apply mt-8;
        }

        ol + p,
        p + p,
        ul + p,
        li + li {
          @apply mt-4;
        }
      }
    }
  }

  .faq {
    @apply flex-col items-center mt-26;

    @screen md {
      @apply mt-46;
    }

    .h2 {
      @apply text-center;
    }

    .accordion {
      @apply mt-14;

      @screen md {
        @apply mt-26;
      }
    }

    .button {
      @apply mt-8;

      @screen md {
        @apply mt-16;
      }
    }
  }

  .push {
    @apply relative justify-center mt-26 bg-blizzard-100 overflow-hidden;

    @screen md {
      @apply mt-46;
    }

    .background {
      @apply absolute inset-0 rounded-none;
    }

    .inner {
      @apply flex-col items-center py-12 z-10;

      @screen md {
        @apply py-16;
      }

      @screen xl {
        max-width: 792px;
      }

      .h3 {
        @apply text-center;
      }

      .h3 + p {
        @apply mt-4 text-grey-blue-700 text-center;
      }

      .button {
        @apply mt-8;
      }
    }
  }

  .get-started {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }

    :deep(.call-to-action) {
      .h2 {
        @apply text-3xl leading-10 !important;

        @screen mdminus {
          @apply text-3xl leading-10 !important;
        }

        @screen mdplus {
          @apply text-5xl leading-16 !important;
        }
      }
    }
  }
}
</style>
